import { download, http } from './request'

/** 案件状态统计（顶部卡片，与新业务阶段一致） */
export interface CaseSummaryStats {
  pending: number
  processing: number
  closeReview: number
  completed: number
  rejected: number
}

/** 列表行状态（库表原始态，展示用 formatCaseFlowStatus） */
export type CaseListStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'coordinating'
  | 'processing'
  | 'evaluating'
  | 'closed'
  | 'archived'

/** 与后端 priority：LOW / NORMAL / HIGH / URGENT / EXTREME 对应 */
export type CaseListPriority = 'low' | 'normal' | 'high' | 'urgent' | 'extreme'

/** 列表项与详情共用核心字段 */
export interface CaseListItem {
  id: string
  caseNo: string
  title: string
  priority: CaseListPriority
  status: CaseListStatus
  submitAt: string
  /** 案件来源：MINI/PHONE/ADMIN（可能为空） */
  caseSource?: string
  /** 来电/登记时间 */
  callTime?: string
  /** 求助类型：PRECISE/SOS */
  helpType?: string
  /** 事件类型 */
  eventType?: string
  petitioner: string
  petitionerId?: string
  enterprise: string
  enterpriseId?: string
  phone: string
  /** 地区名称（接口 regionName） */
  regionName?: string
  /** 地区编码（接口 regionCode） */
  regionCode?: string
  /** 所在国家 */
  countryName?: string
  /** 国家编码（ISO，接口 countryCode） */
  countryCode?: string
  /** 所在位置（接口 locationAddress） */
  locationAddress: string
  content: string
  /** 报案时间（接口 eventTime） */
  eventTime?: string
  /** 求助机构代码 */
  targetInstitution?: string
  /** 附件图片 URL（逗号分隔） */
  attachmentUrls?: string
  /** 备注 */
  remark?: string
  rejectReason?: string
  /** 分配地区名称（接口 assignRegionName） */
  assignRegion?: string
  /** 分配地区编码（接口 assignRegionCode） */
  assignRegionCode?: string
  /** 分配组织 ID（接口 assigneeId） */
  assigneeId?: string
  /** 分配组织名称（接口 assigneeName） */
  assigneeName?: string
  /** 办理层级：COUNTY / CITY / PROVINCE */
  handleLevel?: string
  /** 流转方向：UP / DOWN */
  flowDirection?: string
  /** 立案方向，用来选择向上或向下轨道 */
  entryDirection?: string
  /** 催办标记：NONE / URGING / TIMEOUT */
  urgeMark?: string
  urgeCount?: number
  /** DOMESTIC / OVERSEAS */
  reporterSide?: string
  /** PERSON 个人报案 / ENTERPRISE 企业报案 */
  reporterAccountType?: string
  /** SEND 起点或已回退，直接协助或派发 / DECIDE 接受或驳回 / HANDLE 处理中 */
  taskPhase?: string
  /** ACCEPT / REJECT / ASSIST / DISPATCH / CLOSE，旧数据有 FORWARD */
  taskAction?: string
  taskFinishTime?: string
  /** 任务所在层级，已办里显示已上报、已派发到哪一级 */
  taskLevel?: string
  /** 回退标记：NONE / RETURNED */
  returnMark?: string
  /** 1 表示归属地已锁定在区县，不能再向下派发 */
  regionLocked?: number
  /** 结案申请：NONE / PENDING / APPROVED */
  closeApplyStatus?: string
  /** 结案类型（字典 case_close_type） */
  closeType?: string
  closeResult?: string
  closeRemark?: string
  /** 领事协助信息登记表签章件 */
  registerSignedUrl?: string
  /** 最近一次发起派发或协助的部门 */
  latestForwardOrgId?: string
  /** 部门协同派发记录。待接收 COLLAB_DECIDE，办理中 COLLAB_HANDLE */
  collabId?: string
  /** 协同分派意见，仅部门协同待办、已办有值 */
  collabOpinion?: string
  /** 部门协同被催办次数 */
  collabUrgeCount?: number
  /** 当前登录部门能否接受或驳回 */
  allowDecide?: boolean
  /** 紧急联系类型：WECHAT / PHONE / EMAIL */
  emergencyContactType?: string
  emergencyContactValue?: string
  /** 语音求助文件 URL */
  voiceContentUrl?: string
}

const PRIORITY_LABEL: Record<CaseListPriority, string> = {
  extreme: '极高风险',
  urgent: '紧急',
  high: '高',
  normal: '普通',
  low: '低',
}

/** 案件优先级中文（列表/详情/打印共用） */
export function casePriorityLabel(p: CaseListPriority | string | undefined): string {
  if (p == null || String(p).trim() === '') return ''
  if (typeof p === 'string' && p in PRIORITY_LABEL) return PRIORITY_LABEL[p as CaseListPriority]
  const ui = API_PRIORITY_TO_UI[String(p).trim().toUpperCase()]
  if (ui) return PRIORITY_LABEL[ui]
  return String(p).trim()
}

export function emergencyContactTypeLabel(type: string | undefined): string {
  const s = String(type ?? '')
    .trim()
    .toUpperCase()
  if (s === 'WECHAT') return '微信'
  if (s === 'PHONE') return '电话'
  if (s === 'EMAIL') return '邮箱'
  return String(type ?? '').trim()
}

/** 紧急联系方式一行展示：类型 + 值 */
export function formatEmergencyContactLine(
  type: string | undefined,
  value: string | undefined,
): string {
  const v = String(value ?? '').trim()
  if (!v) return ''
  const t = emergencyContactTypeLabel(type)
  return t ? `${t}：${v}` : v
}

export interface CasePageParams {
  page: number
  pageSize: number
  keyword?: string
  /** 前端筛选项：all 或 CaseListStatus 小写值 */
  status?: string
  /** 分配地区区划码前缀（选省 2 位 / 市 4 位 / 区县 6 位，对应接口 regionCode → assign_region_code LIKE） */
  regionCode?: string
  /** 当前分配部门 ID（assignee_id） */
  orgId?: string
  /** 前端筛选项：all 或 CaseListPriority */
  priority?: string
}

/**
 * 前端筛选值 → 后端 status。
 * 已办结优先传 ARCHIVED（库表真实办结态）：旧后端精确匹配也能查到；
 * 新后端 normalizeFilterStatus(ARCHIVED) 会扩成 ARCHIVED/CLOSED/EVALUATING 整桶。
 */
const UI_STATUS_TO_API: Partial<Record<string, string>> = {
  pending: 'PENDING',
  processing: 'PROCESSING',
  closeReview: 'CLOSE_REVIEW',
  completed: 'ARCHIVED',
  rejected: 'REJECTED',
  // 历史筛选项兼容
  accepted: 'PENDING',
  coordinating: 'PROCESSING',
  evaluating: 'ARCHIVED',
  closed: 'ARCHIVED',
  archived: 'ARCHIVED',
}

/** 前端筛选值 → 后端 priority 大写枚举 */
const UI_PRIORITY_TO_API: Partial<Record<string, string>> = {
  low: 'LOW',
  normal: 'NORMAL',
  high: 'HIGH',
  urgent: 'URGENT',
  extreme: 'EXTREME',
}

const API_STATUS_TO_UI: Record<string, CaseListStatus> = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  COORDINATING: 'coordinating',
  PROCESSING: 'processing',
  EVALUATING: 'evaluating',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
}

const API_PRIORITY_TO_UI: Record<string, CaseListPriority> = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
  EXTREME: 'extreme',
}

export interface CasePageResult {
  list: CaseListItem[]
  total: number
}

/**
 * GET /overseas/case/page 业务 data 体（与统一 ApiResponse 的 data 一致）
 * total 总条数，size 每页条数，current 当前页，records 当前页列表
 */
export interface OverseasCasePageData {
  records: OverseasCaseRecord[]
  total: number
  size: number
  current: number
}

/** 分页 records 单项（与后端字段一致；部分字段可能为空） */
export interface OverseasCaseRecord {
  id?: string
  caseNo?: string
  caseName?: string
  helpType?: string
  eventType?: string
  eventTime?: string
  content?: string
  personnelId?: string
  personnelName?: string
  personnelPhone?: string
  enterpriseId?: string
  enterpriseName?: string
  locationLat?: number
  locationLng?: number
  locationAddress?: string
  regionName?: string
  regionCode?: string
  countryName?: string
  countryCode?: string
  assignOrgName?: string
  targetInstitution?: string
  status?: string
  priority?: string
  assigneeId?: string
  assigneeName?: string
  assignRegionCode?: string
  assignRegionName?: string
  acceptTime?: string
  closeTime?: string
  archiveTime?: string
  remark?: string
  deleteFlag?: string
  createUser?: string
  createTime?: string
  updateUser?: string
  updateTime?: string
  rejectReason?: string
}

/** 后端 GET /overseas/case/statusCount 返回各业务阶段数量（与列表同一套筛选，不含当前状态桶） */
export async function fetchCaseStats(
  params?: Pick<CasePageParams, 'keyword' | 'regionCode' | 'orgId' | 'priority'>,
): Promise<CaseSummaryStats> {
  const query = buildOverseasCasePageQuery({
    page: 1,
    pageSize: 1,
    keyword: params?.keyword,
    regionCode: params?.regionCode,
    orgId: params?.orgId,
    priority: params?.priority,
  })
  delete query.current
  delete query.size
  const raw = await http.get<Record<string, unknown>>('/overseas/case/statusCount', {
    params: query,
  })
  const n = (v: unknown) => {
    const x = Number(v)
    return Number.isFinite(x) ? x : 0
  }
  const completed = n(raw.completed) || n(raw.closed) + n(raw.archived) + n(raw.evaluating)
  const processingRaw = n(raw.processing)
  const closeReview = n(raw.closeReview)
  // 兼容旧接口：processing 含结案审核时，尽量拆不出则整段落在处理中
  return {
    pending: n(raw.pending) + n(raw.accepted),
    processing: processingRaw,
    closeReview,
    completed,
    rejected: n(raw.rejected),
  }
}

function normalizeCasePagePayload(raw: unknown): { list: unknown[]; total: number } {
  if (raw == null || typeof raw !== 'object') return { list: [], total: 0 }
  const o = raw as Record<string, unknown>
  const totalRaw = o.total ?? o.totalCount ?? o.totalElements
  const totalParsed =
    totalRaw === undefined || totalRaw === null
      ? NaN
      : typeof totalRaw === 'number'
        ? totalRaw
        : Number(totalRaw)
  const total = Number.isFinite(totalParsed) ? totalParsed : 0

  if (Array.isArray(o.records)) {
    return {
      list: o.records,
      total: totalRaw !== undefined && totalRaw !== null ? total : o.records.length,
    }
  }
  if (Array.isArray(o.list)) {
    return {
      list: o.list,
      total: totalRaw !== undefined && totalRaw !== null ? total : o.list.length,
    }
  }
  if (Array.isArray(o.rows)) {
    return {
      list: o.rows,
      total: totalRaw !== undefined && totalRaw !== null ? total : o.rows.length,
    }
  }
  return { list: [], total: 0 }
}

function mapOverseasCaseRecordToListItem(raw: unknown): CaseListItem {
  const o = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = o[k]
      if (v != null && String(v).trim() !== '') return String(v).trim()
    }
    return ''
  }
  const statusKey = String(pick('status') || 'PENDING').toUpperCase()
  const status: CaseListStatus = API_STATUS_TO_UI[statusKey] ?? 'pending'
  const priorityKey = String(pick('priority') || 'NORMAL').toUpperCase()
  const priority: CaseListPriority = API_PRIORITY_TO_UI[priorityKey] ?? 'normal'
  const caseSource = pick('caseSource', 'case_source') || undefined
  const callTime = pick('callTime', 'call_time') || undefined
  const helpType = pick('helpType', 'help_type') || undefined
  const eventType = pick('eventType', 'event_type') || undefined
  const locationAddress = pick(
    'locationAddress',
    'location',
    'address',
    'position',
    'location_address',
  )
  const regionName = pick('regionName', 'region', 'areaName', 'district', 'region_name')
  const regionCode = pick('regionCode', 'areaCode', 'region_code') || undefined
  const countryName = pick('countryName', 'country_name') || undefined
  const countryCode = pick('countryCode', 'country_code') || undefined
  const targetInstitution = pick('targetInstitution', 'target_institution') || undefined
  const attachmentUrls = pick('attachmentUrls', 'attachment_urls') || undefined
  const remark = pick('remark') || undefined
  const assigneeId =
    pick('assigneeId', 'assignOrgId', 'assignDepartmentId', 'departmentId') || undefined
  const assigneeName =
    pick('assigneeName', 'assignOrgName', 'assignDepartment', 'targetInstitution', 'department') ||
    undefined
  return {
    id: pick('id') || '',
    caseNo: pick('caseNo', 'caseNumber', 'caseCode', 'case_no'),
    title: pick('caseName', 'title', 'caseTitle', 'case_name'),
    priority,
    status,
    submitAt: pick(
      'createTime',
      'createAt',
      'submitAt',
      'submitTime',
      'gmtCreate',
      'acceptTime',
      'create_time',
    ),
    caseSource,
    callTime,
    helpType,
    eventType,
    petitioner: pick(
      'personnelName',
      'petitioner',
      'seekerName',
      'applicantName',
      'personnel_name',
    ),
    petitionerId: pick('personnelId', 'personnel_id') || undefined,
    enterprise: pick('enterpriseName', 'enterprise', 'companyName', 'corpName', 'enterprise_name'),
    enterpriseId: pick('enterpriseId', 'enterprise_id') || undefined,
    phone: pick('personnelPhone', 'phone', 'contactPhone', 'mobile', 'tel', 'personnel_phone'),
    regionName: regionName || undefined,
    regionCode,
    countryName,
    countryCode,
    locationAddress: locationAddress || regionName || '',
    content: pick('content', 'description', 'detail', 'helpContent'),
    eventTime: pick('eventTime', 'event_time') || undefined,
    targetInstitution,
    attachmentUrls,
    remark,
    rejectReason: pick('rejectReason', 'rejectMsg', 'reject_reason') || undefined,
    assignRegion:
      pick(
        'assignRegionName',
        'assignRegion',
        'assignedRegion',
        'assign_region_name',
        'assign_region',
      ) || undefined,
    assignRegionCode: pick('assignRegionCode', 'assign_region_code') || undefined,
    assigneeId,
    assigneeName,
    handleLevel: pick('handleLevel', 'handle_level') || undefined,
    flowDirection: pick('flowDirection', 'flow_direction') || undefined,
    entryDirection: pick('entryDirection', 'entry_direction') || undefined,
    urgeMark: pick('urgeMark', 'urge_mark') || undefined,
    urgeCount: Number(pick('urgeCount', 'urge_count')) || 0,
    reporterSide: pick('reporterSide', 'reporter_side') || undefined,
    reporterAccountType: pick('reporterAccountType', 'reporter_account_type') || undefined,
    taskPhase: pick('taskPhase', 'task_phase') || undefined,
    collabId: pick('collabId', 'collab_id') || undefined,
    collabOpinion: pick('collabOpinion', 'collab_opinion') || undefined,
    collabUrgeCount: Number(pick('collabUrgeCount', 'collab_urge_count')) || 0,
    taskAction: pick('taskAction', 'task_action') || undefined,
    taskFinishTime: pick('taskFinishTime', 'task_finish_time') || undefined,
    taskLevel: pick('taskLevel', 'task_level') || undefined,
    returnMark: pick('returnMark', 'return_mark') || undefined,
    regionLocked: Number(pick('regionLocked', 'region_locked')) === 1 ? 1 : 0,
    closeApplyStatus: pick('closeApplyStatus', 'close_apply_status') || undefined,
    closeType: pick('closeType', 'close_type') || undefined,
    closeResult: pick('closeResult', 'close_result') || undefined,
    closeRemark: pick('closeRemark', 'close_remark') || undefined,
    registerSignedUrl: pick('registerSignedUrl', 'register_signed_url') || undefined,
    latestForwardOrgId: pick('latestForwardOrgId', 'latest_forward_org_id') || undefined,
    allowDecide:
      o.allowDecide === false || o.allowDecide === 'false'
        ? false
        : o.allowDecide === true || o.allowDecide === 'true'
          ? true
          : undefined,
    emergencyContactType: pick('emergencyContactType', 'emergency_contact_type') || undefined,
    emergencyContactValue: pick('emergencyContactValue', 'emergency_contact_value') || undefined,
    voiceContentUrl: pick('voiceContentUrl', 'voice_content_url') || undefined,
  }
}

/** 与 GET /overseas/case/page、/overseas/case/export 共用的查询参数 */
function buildOverseasCasePageQuery(params: CasePageParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    current: params.page,
    size: params.pageSize,
  }
  const kw = params.keyword?.trim()
  if (kw) query.searchKey = kw
  if (params.status && params.status !== 'all') {
    const raw = String(params.status).trim()
    const st =
      UI_STATUS_TO_API[raw] ||
      UI_STATUS_TO_API[raw.toLowerCase()] ||
      // 误传中文阶段名时仍能筛到
      (raw === '已办结' || raw === '已结案' || raw === '已归档' ? 'COMPLETED' : undefined) ||
      (raw === '结案审核中' || raw === '结案审核' ? 'CLOSE_REVIEW' : undefined)
    if (st) query.status = st
  }
  if (params.priority && params.priority !== 'all') {
    const pr = UI_PRIORITY_TO_API[params.priority]
    if (pr) query.priority = pr
  }
  const regionCode = params.regionCode?.trim()
  if (regionCode) query.regionCode = regionCode
  const orgId = params.orgId?.trim()
  if (orgId) query.orgId = orgId
  return query
}

/**
 * GET /overseas/case/page
 * Query: current, size, searchKey, status, priority, regionCode（分配地区区划码前缀 → assign_region_code）
 */
export async function fetchCaseWorkPage(
  kind: 'todo' | 'done',
  params: CasePageParams,
): Promise<CasePageResult> {
  const raw = await http.get<OverseasCasePageData>(`/overseas/case/${kind}/page`, {
    params: buildOverseasCasePageQuery(params),
  })
  const { list: rows, total } = normalizeCasePagePayload(raw)
  return {
    list: rows.map(mapOverseasCaseRecordToListItem),
    total,
  }
}

export async function fetchCasePage(params: CasePageParams): Promise<CasePageResult> {
  const raw = await http.get<OverseasCasePageData>('/overseas/case/page', {
    params: buildOverseasCasePageQuery(params),
  })
  const { list: rows, total } = normalizeCasePagePayload(raw)
  return {
    list: rows.map(mapOverseasCaseRecordToListItem),
    total,
  }
}

/**
 * GET /overseas/case/export — 导出文件（查询参数与分页接口一致）
 */
export async function exportCaseData(params: CasePageParams): Promise<void> {
  await download('/overseas/case/export', '应急案例导出.xlsx', {
    params: buildOverseasCasePageQuery(params),
  })
}

/** 详情接口 `/overseas/case/detail/{id}` 返回的办案操作历史单条 */
export interface CaseHistoryItem {
  id: string
  caseId?: string
  /** 原状态（后端枚举或文案） */
  fromStatus?: string
  /** 新状态 */
  toStatus?: string
  operatorId?: string
  operatorName?: string
  remark?: string
  /** 该环节上传的附件，多个逗号分隔（协助请示函、结案材料等） */
  attachmentUrls?: string
  createTime?: string
}

/** 详情附件按环节分组 */
export interface CaseAttachmentGroup {
  key: string
  title: string
  hint?: string
  /** 关联历史 id，便于与办理记录对应 */
  historyId?: string
  storedList: string[]
}

function parseStoredFileList(raw?: string | null): string[] {
  return String(raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** 雪花 ID → 毫秒时间（与 MyBatis-Plus / Hutool 默认纪元一致），用于历史附件归类兜底 */
function snowflakeTimeMs(id: string): number | null {
  const s = id.trim()
  if (!/^\d{15,22}$/.test(s)) return null
  try {
    const ts = Number(BigInt(s) >> 22n) + 1288834974657
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

/** 是否为「上级向下派发」办理动作（排除创建时文案里的「户籍派发」） */
function isDispatchActionRemark(remark?: string): boolean {
  const r = String(remark || '')
  if (!r || r.includes('提交求助') || r.includes('户籍派发')) return false
  return r.startsWith('派发') || r.includes('派发至')
}

function isAssistActionRemark(remark?: string): boolean {
  const r = String(remark || '')
  return (
    r.includes('协助（上报）') ||
    r.includes('区县协助') ||
    r.includes('市州协助') ||
    /^协助/.test(r)
  )
}

/** 从历史备注推断协助发起层级（区县→市州，市州→省） */
function resolveAssistLevelLabel(remark?: string, assistOrdinal?: number): string {
  const r = String(remark || '')
  if (r.includes('区县协助') || r.includes('县协助')) return '区县协助请示函'
  if (r.includes('市州协助') || r.includes('市协助')) return '市州协助请示函'
  // 旧数据：上报至省多为市州发起，上报至市多为区县发起
  if (/至[^：:]*省/.test(r)) return '市州协助请示函'
  if (/至[^：:]*[市州]/.test(r)) return '区县协助请示函'
  if (assistOrdinal === 0) return '区县协助请示函'
  if (assistOrdinal === 1) return '市州协助请示函'
  return '协助请示函'
}

function resolveDispatchLevelLabel(remark?: string): string {
  const r = String(remark || '')
  if (r.includes('省派发')) return '省派发附件'
  if (r.includes('市州派发') || r.includes('市派发')) return '市州派发附件'
  return '派发附件'
}

function resolveCloseStageLabel(remark?: string): string {
  const r = String(remark || '')
  if (r.includes('申请结案')) return '申请结案材料'
  if (r.includes('同意结案')) return '同意结案材料'
  if (r.includes('特批')) return '特批结案材料'
  if (r.includes('结案类型') || r.includes('结案')) return '结案材料'
  return '结案材料'
}

function isCloseActionRemark(remark?: string): boolean {
  const r = String(remark || '')
  return (
    r.includes('申请结案') ||
    r.includes('同意结案') ||
    r.includes('结案类型') ||
    (r.includes('结案') && (r.includes('类型') || r.includes('处理结果') || r.includes('材料')))
  )
}

function historyStageTitle(remark?: string, assistOrdinal?: number): string {
  const r = String(remark || '')
  if (r.includes('领事协助信息登记表')) return '领事协助信息登记表'
  if (isAssistActionRemark(r)) return resolveAssistLevelLabel(r, assistOrdinal)
  if (isDispatchActionRemark(r)) return resolveDispatchLevelLabel(r)
  if (isCloseActionRemark(r)) return resolveCloseStageLabel(r)
  if (r.includes('提交求助') || r.includes('创建')) return '用户提交附件'
  return '环节附件'
}

/**
 * 将案件附件按办理环节归类：优先用历史 attachmentUrls；
 * 旧数据未落历史时，按文件雪花时间就近归到协助/派发/提交记录，其余归入案件附件。
 */
export function buildCaseAttachmentGroups(input: {
  attachmentUrls?: string | null
  registerSignedUrl?: string | null
  historyList?: CaseHistoryItem[] | null
}): CaseAttachmentGroup[] {
  const history = [...(input.historyList ?? [])].sort((a, b) =>
    String(a.createTime ?? '').localeCompare(String(b.createTime ?? '')),
  )
  const claimed = new Set<string>()
  const groups: CaseAttachmentGroup[] = []

  const pushGroup = (
    key: string,
    title: string,
    storedList: string[],
    hint?: string,
    historyId?: string,
  ) => {
    const uniq = storedList.filter((s, i, arr) => s && arr.indexOf(s) === i && !claimed.has(s))
    if (!uniq.length) return
    uniq.forEach((s) => claimed.add(s))
    groups.push({ key, title, hint, historyId, storedList: uniq })
  }

  // 协助历史按时间编号，便于旧数据区分区县 / 市州
  const assistOrdinalById = new Map<string, number>()
  let assistSeq = 0
  for (const h of history) {
    if (!isAssistActionRemark(h.remark)) continue
    assistOrdinalById.set(String(h.id), assistSeq++)
  }

  // 1) 历史已挂附件（新数据）；登记表签章并入专用区块，但保留 historyId 供办理记录预览/下载
  let registerHistoryId: string | undefined
  const registerFromHistory: string[] = []
  for (const h of history) {
    const list = parseStoredFileList(h.attachmentUrls)
    if (!list.length) continue
    if (String(h.remark || '').includes('领事协助信息登记表')) {
      registerHistoryId = String(h.id)
      registerFromHistory.push(...list)
      continue
    }
    pushGroup(
      `hist-${h.id}`,
      historyStageTitle(h.remark, assistOrdinalById.get(String(h.id))),
      list,
      h.createTime,
      h.id,
    )
  }

  // 2) 登记表签章件（案件字段 + 历史上传记录合并，挂到对应办理记录）
  pushGroup(
    'register',
    '领事协助信息登记表',
    [...registerFromHistory, ...parseStoredFileList(input.registerSignedUrl)],
    '区县上报前签章件',
    registerHistoryId,
  )

  // 3) 案件总附件里尚未归类的：按雪花时间就近挂到协助/派发/提交历史
  const leftovers = parseStoredFileList(input.attachmentUrls).filter((s) => !claimed.has(s))
  const stageHistories = history.filter((h) => {
    const r = String(h.remark || '')
    return (
      isAssistActionRemark(r) ||
      isDispatchActionRemark(r) ||
      r.includes('提交求助') ||
      isCloseActionRemark(r)
    )
  })
  const bucket = new Map<string, string[]>()
  const unassigned: string[] = []
  for (const stored of leftovers) {
    const fileId = /^https?:\/\//i.test(stored)
      ? stored
          .split('/')
          .pop()
          ?.replace(/\.[^.]+$/, '') || ''
      : stored
    const fileTs = snowflakeTimeMs(fileId)
    if (fileTs == null || !stageHistories.length) {
      unassigned.push(stored)
      continue
    }
    let best: CaseHistoryItem | null = null
    let bestDelta = Number.POSITIVE_INFINITY
    for (const h of stageHistories) {
      const ht = Date.parse(String(h.createTime || ''))
      if (!Number.isFinite(ht)) continue
      const delta = fileTs - ht
      // 文件应在该操作之后生成，允许 2 小时窗口
      if (delta < -5_000 || delta > 2 * 60 * 60 * 1000) continue
      if (delta < bestDelta) {
        bestDelta = delta
        best = h
      }
    }
    if (!best) {
      unassigned.push(stored)
      continue
    }
    const key = `hist-${best.id}`
    const arr = bucket.get(key) || []
    arr.push(stored)
    bucket.set(key, arr)
  }
  for (const h of stageHistories) {
    const key = `hist-${h.id}`
    const list = bucket.get(key)
    if (!list?.length) continue
    const existing = groups.find((g) => g.key === key)
    if (existing) {
      for (const s of list) {
        if (!existing.storedList.includes(s) && !claimed.has(s)) {
          existing.storedList.push(s)
          claimed.add(s)
        }
      }
    } else {
      pushGroup(
        key,
        historyStageTitle(h.remark, assistOrdinalById.get(String(h.id))),
        list,
        h.createTime,
        h.id,
      )
    }
  }
  pushGroup('case-other', '用户提交附件', unassigned, '创建时上传或其他未归类附件')

  return groups
}

const CASE_STATUS_LABEL_ZH: Record<CaseListStatus, string> = {
  pending: '待受理',
  accepted: '已接收',
  rejected: '已拒绝',
  coordinating: '协调中',
  processing: '处理中',
  evaluating: '已办结',
  closed: '已办结',
  archived: '已办结',
}

const LEVEL_ZH: Record<string, string> = {
  COUNTY: '县',
  CITY: '市',
  PROVINCE: '省',
}

/** 待受理/处理中带层级；被下级驳回退回的待受理附加「已回退」，催办附加催办标记 */
export function formatCaseFlowStatus(
  status: CaseListStatus | string | undefined,
  handleLevel?: string,
  urgeMark?: string,
  returnMark?: string,
): string {
  const key = String(status || '').toLowerCase()
  const level = LEVEL_ZH[String(handleLevel || '').toUpperCase()]
  let base =
    CASE_STATUS_LABEL_ZH[key as CaseListStatus] || caseStatusLabelFromApi(String(status || ''))
  if (
    level &&
    (key === 'pending' || key === 'processing' || key === 'accepted' || key === 'coordinating')
  ) {
    if (key === 'accepted' || key === 'coordinating') base = '处理中'
    base = `${base}（${level}）`
  }
  if (key === 'pending' && String(returnMark || '').toUpperCase() === 'RETURNED')
    base = `${base}·已回退`
  if (String(urgeMark || '').toUpperCase() === 'URGING') base = `${base}·催办中`
  if (String(urgeMark || '').toUpperCase() === 'TIMEOUT') base = `${base}·催办超时`
  return base
}

/**
 * 列表/详情/打印统一状态文案：含结案审核中、升级督办（与列表卡片一致）。
 */
export function formatCaseDisplayStatus(row: {
  status?: string
  handleLevel?: string
  urgeMark?: string
  returnMark?: string
  taskPhase?: string
  taskAction?: string
  collabId?: string
  collabUrgeCount?: number
  closeApplyStatus?: string
}): string {
  const phase = String(row.taskPhase || '').toUpperCase()
  const action = String(row.taskAction || '').toUpperCase()
  if (row.collabId) {
    let text = '协同待接收'
    if (phase === 'COLLAB_HANDLE') text = '协同办理中'
    if (action === 'COLLAB_REJECT') text = '已驳回协同'
    if (action === 'COLLAB_DONE') text = '协同已办'
    if ((row.collabUrgeCount || 0) > 0 && !action) text += '·已催办'
    return text
  }
  const closeApply = String(row.closeApplyStatus || '').toUpperCase()
  const status = String(row.status || '').toLowerCase()
  const inCloseReview =
    phase === 'CLOSE_REVIEW' ||
    (closeApply === 'PENDING' && (status === 'processing' || status === 'coordinating'))
  if (inCloseReview) {
    const level = LEVEL_ZH[String(row.handleLevel || '').toUpperCase()]
    let text = level ? `结案审核中（${level}）` : '结案审核中'
    if (String(row.urgeMark || '').toUpperCase() === 'URGING') text += '·催办中'
    if (String(row.urgeMark || '').toUpperCase() === 'TIMEOUT') text += '·催办超时'
    return text
  }
  const base = formatCaseFlowStatus(row.status, row.handleLevel, row.urgeMark, row.returnMark)
  if (phase === 'SUPERVISE') return `${base}·升级督办`
  return base
}

/** 将后端 status 枚举转为中文展示；无法识别时原样返回 */
export function caseStatusLabelFromApi(raw?: string): string {
  if (!raw?.trim()) return '—'
  const key = String(raw).trim().toUpperCase()
  const ui = API_STATUS_TO_UI[key]
  if (ui) return CASE_STATUS_LABEL_ZH[ui]
  return String(raw).trim()
}

function parseHandleOrgList(raw: unknown): CaseHandleOrgItem[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
      const name = String(o.name ?? '').trim()
      const id = String(o.id ?? '').trim()
      const roles = Array.isArray(o.roles)
        ? o.roles.map((x) => String(x || '').trim()).filter(Boolean)
        : []
      return {
        id: id || undefined,
        name: name || id,
        current: o.current === true,
        roles,
        collabStatus: String(o.collabStatus ?? '').trim() || undefined,
      }
    })
    .filter((row) => Boolean(row.name))
}

function parseCaseHistoryList(raw: unknown): CaseHistoryItem[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item, idx) => {
    const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = o[k]
        if (v != null && String(v).trim() !== '') return String(v).trim()
      }
      return ''
    }
    return {
      id: pick('id') || String(idx),
      caseId: pick('caseId') || undefined,
      fromStatus: pick('fromStatus') || undefined,
      toStatus: pick('toStatus') || undefined,
      operatorId: pick('operatorId') || undefined,
      operatorName: pick('operatorName') || undefined,
      remark: pick('remark') || undefined,
      attachmentUrls: pick('attachmentUrls', 'attachment_urls') || undefined,
      createTime: pick('createTime') || undefined,
    }
  })
}

export interface CaseHandleOrgItem {
  id?: string
  name?: string
  current?: boolean
  roles?: string[]
  collabStatus?: string
}

export interface CaseDetailResult {
  item: CaseListItem
  historyList: CaseHistoryItem[]
  handleOrgs: CaseHandleOrgItem[]
}

/**
 * GET /overseas/case/detail/{id}
 * 业务体：`{ caseInfo: { ...与列表 records 同字段 }, historyList: [...] }`
 */
export async function fetchCaseDetail(id: string): Promise<CaseDetailResult | null> {
  try {
    const raw = await http.get<unknown>(`/overseas/case/detail/${encodeURIComponent(id)}`, {
      skipErrorMessage: true,
    })
    if (raw == null || typeof raw !== 'object') return null
    const o = raw as Record<string, unknown>
    const historyList = parseCaseHistoryList(o.historyList)
    const handleOrgs = parseHandleOrgList(o.handleOrgs)
    const caseInfo = o.caseInfo
    const casePayload: unknown = caseInfo != null && typeof caseInfo === 'object' ? caseInfo : raw
    const item = mapOverseasCaseRecordToListItem(casePayload)
    if (!item.id) item.id = id
    return { item, historyList, handleOrgs }
  } catch {
    return null
  }
}

/** POST /overseas/case/updateStatus（结案动作统一 ARCHIVED） */
type OverseasCaseUpdateStatusBody = {
  id: string
  status: 'ARCHIVED'
  /** 结案处理结果 */
  closeResult?: string
  /** 结案类型（字典 case_close_type） */
  closeType?: string
  /** 结案备注 */
  remark?: string
  /** 结案过程附件 */
  attachmentUrls?: string
  /** 归属地未落到区县时，省外办特批结案 */
  specialClose?: boolean
}

/**
 * POST /overseas/case/audit
 * Body: caseId, auditResult（APPROVED/REJECTED）, auditOpinion
 */
export async function acceptCase(id: string): Promise<void> {
  await http.post<void>(`/overseas/case/accept/${encodeURIComponent(id)}`)
}

export async function ackCaseResultNotify(id: string): Promise<void> {
  await http.post<void>(`/overseas/case/resultNotify/ack/${encodeURIComponent(id)}`)
}

export async function rejectCase(id: string, reason: string): Promise<void> {
  await http.post<void>('/overseas/case/reject', {
    caseId: id,
    auditResult: 'REJECTED',
    auditOpinion: reason.trim(),
  })
}

export async function forwardCase(body: {
  id: string
  reason: string
  assigneeId?: string
  assigneeName?: string
  assignRegionCode?: string
  assignRegionName?: string
  attachmentUrls?: string
  sendSms?: boolean
}): Promise<void> {
  await http.post<void>('/overseas/case/forward', body)
}

/** POST /overseas/case/register/upload 上传签章后的《领事协助信息登记表》 */
export async function uploadCaseRegisterSigned(caseId: string, signedUrl: string): Promise<void> {
  await http.post<void>('/overseas/case/register/upload', { caseId, signedUrl })
}

export interface CaseCollabItem {
  id: string
  caseId: string
  institutionId?: string
  institutionName?: string
  institutionType?: string
  opinion?: string
  attachmentUrls?: string
  /** 驻外 SENT/FEEDBACK；部门 PENDING/ACCEPTED/REJECTED/DONE。部门的 institutionType 为 SYS_ORG */
  state?: string
  feedbackContent?: string
  feedbackAttachments?: string
  feedbackTime?: string
  urgeCount?: number
  operatorName?: string
  createTime?: string
}

export interface CaseDisposalItem {
  id: string
  caseId: string
  orgId?: string
  orgName?: string
  content?: string
  attachmentUrls?: string
  operatorName?: string
  createTime?: string
}

export interface CaseCollabInstitutionOption {
  id: string
  name: string
  orgType?: string
  countryName?: string
}

/** 结案类型，字典 case_close_type */
export async function fetchCaseCloseTypeOptions(): Promise<{ label: string; value: string }[]> {
  const raw = await http.get<Record<string, { dictLabel?: string; dictValue?: string }[]>>(
    '/biz/overseas/common/dict/listByType?dictType=case_close_type',
  )
  const rows = Array.isArray(raw?.case_close_type) ? raw.case_close_type : []
  return rows
    .map((d) => ({
      label: String(d.dictLabel ?? '').trim(),
      value: String(d.dictValue ?? '').trim(),
    }))
    .filter((d) => d.value)
}

export async function fetchCaseCollabList(caseId: string): Promise<CaseCollabItem[]> {
  const raw = await http.get<CaseCollabItem[]>('/overseas/case/collab/list', { params: { caseId } })
  return Array.isArray(raw) ? raw : []
}

export async function fetchCaseCollabInstitutions(
  keyword?: string,
): Promise<CaseCollabInstitutionOption[]> {
  const raw = await http.get<CaseCollabInstitutionOption[]>('/overseas/case/collab/institutions', {
    params: keyword?.trim() ? { keyword: keyword.trim() } : {},
  })
  return Array.isArray(raw) ? raw : []
}

export async function sendCaseCollab(body: {
  caseId: string
  institutionIds?: string[]
  orgIds?: string[]
  opinion: string
  attachmentUrls?: string
}): Promise<void> {
  await http.post<void>('/overseas/case/collab/send', body)
}

export async function acceptCaseCollab(collabId: string): Promise<void> {
  await http.post<void>('/overseas/case/collab/accept', { collabId })
}

export async function rejectCaseCollab(collabId: string, reason: string): Promise<void> {
  await http.post<void>('/overseas/case/collab/reject', { collabId, reason })
}

export async function completeCaseCollab(collabId: string): Promise<void> {
  await http.post<void>('/overseas/case/collab/complete', { collabId })
}

export async function feedbackCaseCollab(body: {
  collabId: string
  feedbackContent: string
  feedbackAttachments?: string
}): Promise<void> {
  await http.post<void>('/overseas/case/collab/feedback', body)
}

export async function urgeCaseCollab(collabId: string): Promise<void> {
  await http.post<void>(`/overseas/case/collab/urge/${encodeURIComponent(collabId)}`)
}

export async function fetchCaseDisposalList(caseId: string): Promise<CaseDisposalItem[]> {
  const raw = await http.get<CaseDisposalItem[]>('/overseas/case/disposal/list', {
    params: { caseId },
  })
  return Array.isArray(raw) ? raw : []
}

export async function addCaseDisposal(body: {
  caseId: string
  content: string
  attachmentUrls?: string
}): Promise<void> {
  await http.post<void>('/overseas/case/disposal', body)
}

/**
 * POST /overseas/case/urge/{id}
 */
export async function urgeCase(
  id: string,
  body: { reason: string; requirement: string; deadline: string },
): Promise<void> {
  await http.post<void>(`/overseas/case/urge/${encodeURIComponent(id)}`, body)
}

export type CaseFlowNodeState = 'done' | 'current' | 'pending' | 'rejected'

export interface CaseFlowNode {
  key: string
  label: string
  state: CaseFlowNodeState
}

type RailStep = { key: string; label: string; status: string; level: string }

/** 境内：区县起点不用接受，直接上报到市州。 */
const DOMESTIC_RAIL: RailStep[] = [
  { key: 'c-p', label: '待受理（县）', status: 'PENDING', level: 'COUNTY' },
  { key: 'y-p', label: '待受理（市）', status: 'PENDING', level: 'CITY' },
  { key: 'y-h', label: '处理中（市）', status: 'PROCESSING', level: 'CITY' },
  { key: 'p-p', label: '待受理（省）', status: 'PENDING', level: 'PROVINCE' },
  { key: 'p-h', label: '处理中（省）', status: 'PROCESSING', level: 'PROVINCE' },
  { key: 'close', label: '结案', status: 'CLOSED', level: '' },
  { key: 'arch', label: '归档', status: 'ARCHIVED', level: '' },
]

/** 境外：省外办起点不用接受，直接派发；区县收到派发后转为向上请求协助。 */
const OVERSEAS_RAIL: RailStep[] = [
  { key: 'p-p', label: '待受理（省）', status: 'PENDING', level: 'PROVINCE' },
  { key: 'y-p', label: '待受理（市）', status: 'PENDING', level: 'CITY' },
  { key: 'y-h', label: '处理中（市）', status: 'PROCESSING', level: 'CITY' },
  { key: 'c-p', label: '待受理（县）', status: 'PENDING', level: 'COUNTY' },
  { key: 'c-h', label: '处理中（县）', status: 'PROCESSING', level: 'COUNTY' },
  { key: 'y-up-p', label: '待受理（市·上报）', status: 'PENDING', level: 'CITY' },
  { key: 'y-up-h', label: '处理中（市·上报）', status: 'PROCESSING', level: 'CITY' },
  { key: 'p-up-p', label: '待受理（省·上报）', status: 'PENDING', level: 'PROVINCE' },
  { key: 'p-up-h', label: '处理中（省·上报）', status: 'PROCESSING', level: 'PROVINCE' },
  { key: 'close', label: '结案', status: 'CLOSED', level: '' },
  { key: 'arch', label: '归档', status: 'ARCHIVED', level: '' },
]

/** V8 进度：已到达、当前、未到达都列出。境外先向下派发，到区县后转向上请求协助。 */
export function buildCaseFlowRail(input: {
  status?: string
  handleLevel?: string
  flowDirection?: string
  entryDirection?: string
  reporterSide?: string
}): CaseFlowNode[] {
  let status = String(input.status || '').toUpperCase()
  if (status === 'ACCEPTED' || status === 'COORDINATING') status = 'PROCESSING'
  const level = String(input.handleLevel || 'COUNTY').toUpperCase()
  const direction = String(input.flowDirection || 'UP').toUpperCase()
  const entry = String(input.entryDirection || '').toUpperCase()
  const overseas = entry === 'DOWN' || String(input.reporterSide || '').toUpperCase() === 'OVERSEAS'
  const rail = overseas ? OVERSEAS_RAIL : DOMESTIC_RAIL
  let index = 0
  if (status === 'ARCHIVED') {
    index = rail.length - 1
  } else if (status === 'CLOSED' || status === 'EVALUATING') {
    // 已办结/评价中：进度落在结案节点（勿回落到起点）
    index = rail.findIndex((s) => s.status === 'CLOSED')
    if (index < 0) index = Math.max(0, rail.length - 2)
  } else if (status === 'REJECTED') {
    index = rail.findIndex((s) => s.status === 'PENDING' && s.level === level)
    if (index < 0) index = 0
  } else if (overseas && direction === 'UP') {
    const upward = rail.findIndex((s) => s.key === 'c-p')
    const hit = rail.findIndex((s, i) => i >= upward && s.status === status && s.level === level)
    index = hit >= 0 ? hit : upward
  } else {
    const hit = rail.findIndex((s) => s.status === status && s.level === level)
    index = hit >= 0 ? hit : 0
  }
  return rail.map((step, i) => ({
    key: step.key,
    label: status === 'REJECTED' && i === index ? '驳回' : step.label,
    state:
      status === 'REJECTED' && i === index
        ? 'rejected'
        : i < index
          ? 'done'
          : i === index
            ? 'current'
            : 'pending',
  }))
}

/**
 * POST /overseas/case/updateStatus
 * Body: id, status 固定 ARCHIVED（申请结案 / 同意结案 / 直接结案均由此入口，服务端按待办分流）
 */
export async function closeCase(
  id: string,
  material?: {
    closeResult: string
    closeType: string
    remark?: string
    attachmentUrls?: string
    specialClose?: boolean
  },
): Promise<void> {
  const body: OverseasCaseUpdateStatusBody = {
    id,
    status: 'ARCHIVED',
    ...(material?.closeResult ? { closeResult: material.closeResult } : {}),
    ...(material?.closeType ? { closeType: material.closeType } : {}),
    ...(material?.remark ? { remark: material.remark } : {}),
    ...(material?.attachmentUrls ? { attachmentUrls: material.attachmentUrls } : {}),
    ...(material?.specialClose ? { specialClose: true } : {}),
  }
  await http.post<void>('/overseas/case/updateStatus', body)
}

/**
 * POST /overseas/case/delete/{id}
 * 逻辑删除案件（后端通过 deleteFlag 标记删除）
 */
export async function deleteCase(id: string): Promise<void> {
  await http.post<void>(`/overseas/case/delete/${encodeURIComponent(id)}`)
}

/** POST /overseas/case/create 请求体（与后端字段一致） */
export type OverseasCaseCreatePriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT' | 'EXTREME'

export interface OverseasCaseCreateBody {
  caseTitle: string
  /** 案件来源：MINI/PHONE/ADMIN */
  caseSource?: string
  /** 来电/登记时间（yyyy-MM-dd HH:mm:ss） */
  callTime?: string
  priority: OverseasCaseCreatePriority
  regionName: string
  regionCode: string
  /** 求助人ID */
  personnelId?: string
  personnelName: string
  contactPhone: string
  /** 所属企业ID */
  enterpriseId?: string
  enterpriseName: string
  locationAddress: string
  content: string
  voiceContentUrl?: string
  emergencyContactType?: string
  emergencyContactValue?: string
  countryName: string
  countryCode: string
  /**
   * 事发是否在中国境内（含港澳台）。后台代录显式选择后传入，优先于经纬度/国家推断。
   */
  locationWithinChina?: boolean
  /** 求助类型：PRECISE/SOS */
  helpType?: string
  /** 事件类型 */
  eventType?: string
  /** 求助机构代码 */
  targetInstitution?: string
  /** 附件图片 URL（逗号分隔） */
  attachmentUrls?: string
  /** 非必填 */
  remark?: string
  /** 报案时间 */
  eventTime: string
  /** 分配地区编码（无人员户籍时必填） */
  assignRegionCode?: string
  assignRegionName?: string
}

export interface OverseasCaseUpdateBody extends OverseasCaseCreateBody {
  id: string
}

/** 与后端 caseSource 枚举一致：PHONE / ADMIN / MINI */
export const OVERSEAS_CASE_DEFAULT_SOURCE = 'PHONE'
export const OVERSEAS_CASE_DEFAULT_HELP_TYPE = 'PRECISE'

/**
 * POST /overseas/case/create
 */
export async function createCase(body: OverseasCaseCreateBody): Promise<void> {
  const { remark, caseSource, helpType, eventType, ...rest } = body
  await http.post<void>('/overseas/case/create', {
    ...rest,
    caseSource: caseSource ?? OVERSEAS_CASE_DEFAULT_SOURCE,
    helpType: helpType ?? OVERSEAS_CASE_DEFAULT_HELP_TYPE,
    ...(eventType != null && String(eventType).trim() !== ''
      ? { eventType: String(eventType).trim() }
      : {}),
    ...(remark != null && String(remark).trim() !== '' ? { remark: String(remark).trim() } : {}),
  })
}

/**
 * POST /overseas/case/update
 */
export async function updateCase(body: OverseasCaseUpdateBody): Promise<void> {
  const { remark, caseSource, helpType, eventType, ...rest } = body
  await http.post<void>('/overseas/case/update', {
    ...rest,
    caseSource: caseSource ?? OVERSEAS_CASE_DEFAULT_SOURCE,
    helpType: helpType ?? OVERSEAS_CASE_DEFAULT_HELP_TYPE,
    ...(eventType != null && String(eventType).trim() !== ''
      ? { eventType: String(eventType).trim() }
      : {}),
    ...(remark != null && String(remark).trim() !== '' ? { remark: String(remark).trim() } : {}),
  })
}
